/**
 * Downloading an export.
 *
 * The endpoint needs the Authorization header, so an export cannot be a plain
 * link — it is fetched as a blob and handed to the browser as an object URL.
 * That detail is the same everywhere an export is offered, so it lives here
 * rather than in each page that offers one.
 */
import { client } from '../api/client';

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportRequest {
  kind: string;
  format?: ExportFormat;
  /** Any filters the endpoint accepts, in camelCase — the interceptor converts. */
  params?: Record<string, string | undefined>;
}

/**
 * Read the message out of an error whose body is a Blob.
 *
 * With responseType 'blob' even a 422 arrives as binary, so the ordinary error
 * helper sees an unreadable body and falls back to something generic — losing
 * the one message that tells the user what to do next ("narrow it with a date
 * range").
 */
export async function exportErrorMessage(
  error: unknown,
  fallback = 'Could not build that export.',
): Promise<string> {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text()) as { detail?: unknown };
      if (typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      // Not JSON — fall through to the generic message.
    }
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

/** Use the filename the server chose rather than inventing one here. */
function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = disposition?.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

/**
 * Fetch an export and hand it to the browser as a download.
 *
 * Resolves with the filename saved, so a caller can name it in a toast.
 */
export async function downloadExport({
  kind,
  format = 'csv',
  params = {},
}: ExportRequest): Promise<string> {
  const response = await client.get(`/api/v1/exports/${kind}`, {
    responseType: 'blob',
    params: {
      ...(format === 'xlsx' ? { format } : {}),
      ...Object.fromEntries(
        Object.entries(params).filter(([, value]) => value),
      ),
    },
  });

  const name = filenameFrom(
    response.headers['content-disposition'] as string | undefined,
    `${kind}.${format}`,
  );

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
  return name;
}
